/**
 * UI Controls for the Pisa Tower simulation
 */

import type { Controls as IControls, SimulationParams } from '../types';

export class Controls implements IControls {
  private params: SimulationParams;
  private paramChangeCallbacks: Array<(params: Partial<SimulationParams>) => void> = [];
  private playCallbacks: Array<() => void> = [];
  private pauseCallbacks: Array<() => void> = [];
  private resetCallbacks: Array<() => void> = [];

  private playPauseBtn: HTMLButtonElement;
  private resetBtn: HTMLButtonElement;
  private initialBtn: HTMLButtonElement;
  private isPlaying = false;

  constructor() {
    // Initialize parameters with fixed values and sliders
    this.params = {
      blueDiameter: 0.2,  // fixed at 0.2m
      blueDensity: this.getSliderValue('blueDensity'),
      redDiameter: 0.2,   // fixed at 0.2m
      redDensity: this.getSliderValue('redDensity'),
      gravity: 9.81,      // fixed at Earth gravity
      airDensity: 1.225,  // fixed at sea level
      planeAngle: 8,      // fixed at 8 degrees
      frictionCoefficient: this.getSliderValue('frictionCoefficient'),
      timeScale: 1.0      // fixed at real-time
    };

    // Get button elements
    this.playPauseBtn = document.getElementById('playPauseBtn') as HTMLButtonElement;
    this.resetBtn = document.getElementById('resetBtn') as HTMLButtonElement;
    this.initialBtn = document.getElementById('initialBtn') as HTMLButtonElement;

    // Set up event listeners
    this.setupSliders();
    this.setupButtons();
    this.updateAllDisplays();
  }

  private getSliderValue(id: string): number {
    const slider = document.getElementById(id) as HTMLInputElement;
    return parseFloat(slider.value);
  }

  private setupSliders(): void {
    // Blue ball density
    this.setupSlider('blueDensity', (value) => {
      this.updateDisplay('blueDensityValue', `${Math.round(value)} kg/m³`);
      this.notifyParamChange({ blueDensity: value });
    });

    // Red ball density
    this.setupSlider('redDensity', (value) => {
      this.updateDisplay('redDensityValue', `${Math.round(value)} kg/m³`);
      this.notifyParamChange({ redDensity: value });
    });

    // Friction coefficient
    this.setupSlider('frictionCoefficient', (value) => {
      this.updateDisplay('frictionCoefficientValue', `${value.toFixed(2)}`);
      this.notifyParamChange({ frictionCoefficient: value });
    });
  }

  private setupSlider(id: string, onChange: (value: number) => void): void {
    const slider = document.getElementById(id) as HTMLInputElement;
    slider.addEventListener('input', () => {
      const value = parseFloat(slider.value);
      onChange(value);
    });
  }

  private setupButtons(): void {
    this.playPauseBtn.addEventListener('click', () => {
      if (this.isPlaying) {
        this.isPlaying = false;
        this.playPauseBtn.textContent = 'Play';
        this.pauseCallbacks.forEach(cb => cb());
      } else {
        this.isPlaying = true;
        this.playPauseBtn.textContent = 'Pause';
        this.playCallbacks.forEach(cb => cb());
      }
    });

    this.resetBtn.addEventListener('click', () => {
      this.isPlaying = false;
      this.playPauseBtn.textContent = 'Play';
      this.resetCallbacks.forEach(cb => cb());
    });

    this.initialBtn.addEventListener('click', () => {
      this.resetToInitialValues();
      this.isPlaying = false;
      this.playPauseBtn.textContent = 'Play';
      this.resetCallbacks.forEach(cb => cb());
    });
  }

  private updateDisplay(id: string, value: string): void {
    const element = document.getElementById(id);
    if (element) {
      element.textContent = value;
    }
  }

  private updateAllDisplays(): void {
    this.updateDisplay('blueDensityValue', `${Math.round(this.params.blueDensity)} kg/m³`);
    this.updateDisplay('redDensityValue', `${Math.round(this.params.redDensity)} kg/m³`);
    this.updateDisplay('frictionCoefficientValue', `${this.params.frictionCoefficient.toFixed(2)}`);
  }

  private resetToInitialValues(): void {
    // Reset each slider to its data-initial value
    const sliders = ['blueDensity', 'redDensity', 'frictionCoefficient'];

    sliders.forEach(id => {
      const slider = document.getElementById(id) as HTMLInputElement;
      const initialValue = slider.getAttribute('data-initial');

      if (initialValue) {
        slider.value = initialValue;

        // Trigger the input event to update display and parameters
        const event = new Event('input', { bubbles: true });
        slider.dispatchEvent(event);
      }
    });
  }

  private notifyParamChange(params: Partial<SimulationParams>): void {
    // Update internal params
    Object.assign(this.params, params);

    // Notify callbacks
    this.paramChangeCallbacks.forEach(cb => cb(params));
  }

  onParamChange(callback: (params: Partial<SimulationParams>) => void): void {
    this.paramChangeCallbacks.push(callback);
  }

  onPlay(callback: () => void): void {
    this.playCallbacks.push(callback);
  }

  onPause(callback: () => void): void {
    this.pauseCallbacks.push(callback);
  }

  onReset(callback: () => void): void {
    this.resetCallbacks.push(callback);
  }

  updatePlayButton(playing: boolean, complete: boolean): void {
    this.isPlaying = playing;

    if (complete) {
      this.playPauseBtn.textContent = 'Play';
      this.playPauseBtn.disabled = true;
      this.isPlaying = false;
    } else {
      this.playPauseBtn.disabled = false;
      this.playPauseBtn.textContent = playing ? 'Pause' : 'Play';
    }
  }

  getParams(): SimulationParams {
    return { ...this.params };
  }
}
